import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { WorkflowTestRunItem, DiffField } from "../../services/WorkflowService";

interface Props {
  item: WorkflowTestRunItem | null;
  isVisible: boolean;
  onClose: () => void;
}

function formatJson(obj: unknown): string {
  if (obj === null || obj === undefined) return "—";
  if (typeof obj === "string") {
    try {
      return JSON.stringify(JSON.parse(obj), null, 2);
    } catch {
      return obj;
    }
  }
  return JSON.stringify(obj, null, 2);
}

function formatFieldValue(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "string") return val;
  return JSON.stringify(val);
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  match: { label: "Match", color: "text-green-600 dark:text-green-400", bg: "bg-green-50/50 dark:bg-green-900/10" },
  mismatch: { label: "Mismatch", color: "text-red-600 dark:text-red-400", bg: "bg-red-50/50 dark:bg-red-900/10" },
  missing: { label: "Missing", color: "text-red-600 dark:text-red-400", bg: "bg-red-50/50 dark:bg-red-900/10" },
  extra: { label: "Extra", color: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-50/50 dark:bg-yellow-900/10" },
};

const FieldDiffTable: React.FC<{ fields: DiffField[] }> = ({ fields }) => {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Field</th>
            <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Expected</th>
            <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Actual</th>
            <th className="text-center py-2.5 px-3 font-medium text-muted-foreground w-[100px]">Status</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => {
            const config = statusConfig[field.status] || statusConfig.mismatch;
            return (
              <tr key={field.path} className={cn("border-b border-border/50", config.bg)}>
                <td className="py-2 px-3 font-mono text-xs text-foreground">{field.path}</td>
                <td className="py-2 px-3 font-mono text-xs text-muted-foreground max-w-[200px]">
                  <span className="block truncate" title={formatFieldValue(field.expected)}>
                    {formatFieldValue(field.expected)}
                  </span>
                </td>
                <td className="py-2 px-3 font-mono text-xs text-muted-foreground max-w-[200px]">
                  <span className="block truncate" title={formatFieldValue(field.actual)}>
                    {formatFieldValue(field.actual)}
                  </span>
                </td>
                <td className={cn("py-2 px-3 text-center text-xs font-medium", config.color)}>
                  {config.label}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const WorkflowTestRunItemComparison: React.FC<Props> = ({ item, isVisible, onClose }) => {
  if (!item) return null;

  const resultIcon = item.matched === true
    ? <CheckCircleIcon className="w-5 h-5 text-green-500" />
    : item.status === "ERROR"
    ? <AlertCircleIcon className="w-5 h-5 text-orange-500" />
    : <XCircleIcon className="w-5 h-5 text-red-500" />;

  const resultLabel = item.matched === true ? "Passed" : item.status === "ERROR" ? "Error" : "Failed";
  const diff = item.diff_result;
  const matchRate = diff ? Math.round(diff.match_rate * 100) : null;

  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl w-full max-h-[90vh] overflow-y-auto bg-background border-border">
        <DialogHeader className="pb-4 border-b border-border">
          <DialogTitle className="text-xl font-gc-med text-foreground flex items-center gap-2">
            Item Comparison
            <span className="flex items-center gap-1.5">
              {resultIcon}
              <Badge
                variant="outline"
                className={
                  item.matched === true
                    ? "border-green-300 text-green-700 dark:text-green-400"
                    : item.status === "ERROR"
                    ? "border-orange-300 text-orange-700 dark:text-orange-400"
                    : "border-red-300 text-red-700 dark:text-red-400"
                }
              >
                {resultLabel}
              </Badge>
            </span>
          </DialogTitle>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
            {item.duration_ms != null && <span>Duration: {item.duration_ms}ms</span>}
            {diff && (
              <span className={cn(
                "font-medium",
                matchRate === 100 ? "text-green-600" : matchRate != null && matchRate >= 80 ? "text-yellow-600" : "text-red-600"
              )}>
                {diff.matched_count}/{diff.total_count} fields match ({matchRate}%)
              </span>
            )}
          </div>
        </DialogHeader>

        {/* Input section */}
        {item.input && (
          <div className="mt-4">
            <h3 className="text-sm font-gc-med text-muted-foreground mb-2">Input</h3>
            <pre className="text-xs font-mono bg-muted/50 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto">
              {formatJson(item.input)}
            </pre>
          </div>
        )}

        {/* Field-level diff table (primary view when diff is available) */}
        {diff && diff.fields.length > 0 && item.status !== "ERROR" && (
          <div className="mt-4">
            <h3 className="text-sm font-gc-med text-muted-foreground mb-2">Field Comparison</h3>
            <FieldDiffTable fields={diff.fields} />
          </div>
        )}

        {/* Raw JSON comparison (fallback when no diff, or for error states) */}
        {(!diff || item.status === "ERROR") && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-gc-med text-muted-foreground mb-2">Expected Output</h3>
              <pre className="text-xs font-mono bg-green-50 dark:bg-green-900/10 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto border border-green-200 dark:border-green-900/30">
                {formatJson(item.expected_output)}
              </pre>
            </div>
            <div>
              <h3 className="text-sm font-gc-med text-muted-foreground mb-2">Actual Output</h3>
              {item.status === "ERROR" ? (
                <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-4 border border-red-200 dark:border-red-900/30">
                  <p className="text-xs font-mono text-red-600 dark:text-red-400 whitespace-pre-wrap break-all">
                    {item.error_message || "Unknown error"}
                  </p>
                </div>
              ) : (
                <pre className="text-xs font-mono bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto border border-blue-200 dark:border-blue-900/30">
                  {formatJson(item.actual_output)}
                </pre>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowTestRunItemComparison;
